import { URDFJoint, URDFRobot } from "urdf-loader";
import {
  ArmJointConfig,
  ArmJointRole,
  ArmMotionMapper,
  Model,
} from "../../models/Model";
import { PropValueLine } from "../common/PropValueLine";
import { TextField, MenuItem, Checkbox } from "@mui/material";
import { useEffect, useState } from "react";
import * as THREE from "three";
import { Vec3Editor } from "../common/Vec3Editor";
import { copyVec3 } from "../../visuals/visual-utils";

export function URDFRobotArmJoint({
  robot,
  joint,
  model,
}: {
  robot: URDFRobot;
  joint: URDFJoint;
  model: Model;
}) {
  const [config, setConfig] = useState<ArmJointConfig>();

  useEffect(() => {
    if (!joint) {
      setConfig(undefined);
      return;
    }
    const mapper = model.motionMapper as ArmMotionMapper;
    const match = Object.values(mapper.joints).find(
      (c) => c.name === joint.name && c.role !== ArmJointRole.None
    );
    if (match) {
      match.offset = match.offset || { x: 0, y: 0, z: 0 };
      if (!match.offset.x) match.offset.x = 0;
      if (!match.offset.y) match.offset.y = 0;
      if (!match.offset.z) match.offset.z = 0;
      joint.setJointValue(match.zeroAngle);
      setConfig(match);
      return;
    }
    setConfig({
      name: joint.name,
      role: ArmJointRole.None,
      zeroAngle: 0,
      invert: false,
      offset: { x: 0, y: 0, z: 0 },
    });
  }, [joint]);
  if (!config) return null;

  const mixer = (update: Partial<ArmJointConfig>) => {
    const mapper = model.motionMapper as ArmMotionMapper;
    const roleOrder = [
      ArmJointRole.BaseZ,
      ArmJointRole.BaseY,
      ArmJointRole.ElbowY,
      ArmJointRole.WristZ,
      ArmJointRole.BusinessEnd,
    ];

    const newConfig = { ...config, ...update };
    const index = roleOrder.indexOf(newConfig.role);
    const posRobot = robot.getWorldPosition(new THREE.Vector3());
    const posJoint = joint.getWorldPosition(new THREE.Vector3());
    let offset = new THREE.Vector3().subVectors(posJoint, posRobot);
    if (index > 0) {
      const prevRole = roleOrder[index - 1];
      const prevConfig = mapper.joints[prevRole];
      if (prevConfig) {
        const prevJoint = robot.joints[prevConfig.name];
        if (prevJoint) {
          const posPrevJoint = prevJoint.getWorldPosition(new THREE.Vector3());
          offset.sub(new THREE.Vector3().subVectors(posPrevJoint, posRobot));
        }
      }
    }
    copyVec3(offset, newConfig.offset);
    setConfig(newConfig);
    mapper.joints[newConfig.role] = newConfig;
  };

  return (
    <>
      <PropValueLine
        label="Role"
        value={
          <TextField
            size="small"
            variant="standard"
            select
            value={config.role}
            onChange={(e) => {
              mixer({ role: e.target.value as ArmJointRole });
            }}
            sx={{ minWidth: "200px" }}
          >
            {Object.values(ArmJointRole).map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
        }
      />
      <PropValueLine
        label="Zero Angle"
        value={
          <TextField
            size="small"
            variant="standard"
            type="number"
            value={THREE.MathUtils.radToDeg(config.zeroAngle)}
            onChange={(e) => {
              const rads = THREE.MathUtils.degToRad(parseFloat(e.target.value));
              mixer({ zeroAngle: rads });
              joint.setJointValue(rads);
            }}
            sx={{ minWidth: "200px" }}
          />
        }
      />
      <PropValueLine
        label="Invert"
        value={
          <Checkbox
            size="small"
            sx={{ height: "20px", width: "20px" }}
            checked={config.invert}
            onChange={(e, checked) => {
              mixer({ invert: checked });
            }}
          />
        }
      />
      <PropValueLine
        label="Offset"
        value={
          <Vec3Editor
            value={config.offset}
            onChange={(offset) => {
              mixer({ offset });
            }}
          />
        }
      />
    </>
  );
}
